import React, {forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,} from "react";

import {CustomOverlay, OpenLayers, useCluster} from "@delivus-daas/react-open-layers";
import {ShippingsTooltip} from "./ShippingsTooltip";
import {useShippings} from "../../hooks/useShippings";
import {useSearchParams} from "react-router-dom";
import {RefreshSvg} from "shared/assets/svgs/RefreshSvg";
import {useMapFunctions} from "shared/hooks/useMapFunctions";
import {Map, MapEvent} from "ol";
import TileLayer from "ol/layer/Tile";
import {XYZ} from "ol/source";
import debounce from "lodash.debounce";
import {clusterOverStyle, clusterStyle} from "shared/lib/map.fns";
import Feature from "ol/Feature";
import {SelectEvent} from "ol/interaction/Select";
import {Button} from "shared/ui";
import {ShippingSearchParamKey, TShippingItem} from "features/shipping/types";
import {DEFAULT_MAP_OPTIONS, MAX_ZOOM, MIN_ZOOM, SEOUL_CENTER, ZOOM} from "shared/constants/consts.common";
import {TShippingTooltip} from "shared/types";

interface Props {
  onSelectShippings: (shippings: TShippingItem[]) => void;
}

export interface ShippingMapRef {
  resetSelection: () => void;
  refresh: () => void;
}

const ShippingMap = forwardRef<ShippingMapRef, Props>(({onSelectShippings}: Props, ref) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsRef = useRef<URLSearchParams>();
  const lastSavedMapStateRef = useRef("");
  const [map, setMap] = useState<Map>();
  const {
          mouseOverCluster, clickCluster, resetSelectedShippings
        } = useMapFunctions<TShippingItem>({onSelectShippings});
  const [hoveredShippingTooltip, setHoveredShippingTooltip] = useState<TShippingTooltip<TShippingItem>>();
  const {features, mutate} = useShippings();

  useEffect(() => {
    paramsRef.current = searchParams;
    lastSavedMapStateRef.current = [
      searchParams.get(ShippingSearchParamKey.mapCenterLng) ?? "",
      searchParams.get(ShippingSearchParamKey.mapCenterLat) ?? "",
      searchParams.get(ShippingSearchParamKey.mapZoom) ?? "",
    ].join("|");
  }, [searchParams]);

  const center = useMemo(() => {
    const latValue = searchParams.get(ShippingSearchParamKey.mapCenterLat);
    const lngValue = searchParams.get(ShippingSearchParamKey.mapCenterLng);

    if (latValue !== null && lngValue !== null) {
      const lat = Number(latValue);
      const lng = Number(lngValue);

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return [lng, lat];
      }
    }

    return SEOUL_CENTER;
  }, [searchParams]);

  const zoom = useMemo(() => {
    const mapZoomValue = searchParams.get(ShippingSearchParamKey.mapZoom);

    if (mapZoomValue !== null) {
      const mapZoom = Number(mapZoomValue);

      if (Number.isFinite(mapZoom)) {
        return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, mapZoom));
      }
    }

    return ZOOM;
  }, [searchParams]);
  
  const handleMouseOverCluster = useCallback((selected: Feature[], deselected: Feature[], event: SelectEvent) => {
    mouseOverCluster(selected, deselected, event, setHoveredShippingTooltip)
  }, [])
  
  useCluster({
    features,
    map,
    zoom,
    onClick: clickCluster,
    onOver: handleMouseOverCluster,
    clusterStyle: clusterStyle,
    overStyle: clusterOverStyle,
    layerOptions: {zIndex: 15}
  });
  
  const handleRefresh = useCallback(() => {
    mutate()
  }, [mutate]);
  
  const handleResetSelection = useCallback(() => {
    resetSelectedShippings();
  }, [setSearchParams]);
  
  useImperativeHandle<ShippingMapRef, ShippingMapRef>(ref, () => ({
    resetSelection: handleResetSelection, refresh: handleRefresh,
  }), [handleResetSelection, handleRefresh]);
  
  const syncMapSearchParams = useMemo(() => debounce((currentMap: Map) => {
    const view = currentMap.getView();
    const nextCenter = view.getCenter() as number[] | undefined;
    const nextZoom = view.getZoom();

    if (!nextCenter || !Number.isFinite(nextCenter[0]) || !Number.isFinite(nextCenter[1]) || !Number.isFinite(nextZoom)) {
      return;
    }

    const nextLng = nextCenter[0].toFixed(4);
    const nextLat = nextCenter[1].toFixed(4);
    const nextZoomValue = String(Math.round((nextZoom ?? ZOOM) * 100) / 100);
    const nextMapStateKey = [nextLng, nextLat, nextZoomValue].join("|");

    if (lastSavedMapStateRef.current === nextMapStateKey) {
      return;
    }

    lastSavedMapStateRef.current = nextMapStateKey;

    setSearchParams((prev) => {
      const params = new URLSearchParams(paramsRef.current ?? prev);
      params.set(ShippingSearchParamKey.mapCenterLng, nextLng);
      params.set(ShippingSearchParamKey.mapCenterLat, nextLat);
      params.set(ShippingSearchParamKey.mapZoom, nextZoomValue);
      return params;
    }, {replace: true});
  }, 600), [setSearchParams]);

  useEffect(() => {
    return () => {
      syncMapSearchParams.cancel();
    };
  }, [syncMapSearchParams]);

  const handleMapBoundChanged = useCallback((event: MapEvent) => {
    const currentMap = event.map;

    if (!currentMap) {
      return;
    }

    syncMapSearchParams(currentMap);
  }, [syncMapSearchParams]);
  
  const vworldBaseLayer = useMemo(() => new TileLayer({
    source: new XYZ({
      url: `http://api.vworld.kr/req/wmts/1.0.0/${process.env.REACT_APP_MAP_KEY}/white/{z}/{y}/{x}.png`,
    }), properties: {name: "base-vworld-base"}, preload: Infinity,
  }), []);
  
  return (<div className={"map-container"}>
    <OpenLayers
      initialLayers={[vworldBaseLayer]}
      initialInteractionOptions={{pinchZoom: true}}
      initialViewOptions={{zoom, center: center ?? SEOUL_CENTER, ...DEFAULT_MAP_OPTIONS}}
      showZoom
      onInit={setMap}
      onMoveEnd={handleMapBoundChanged}
    >
      <div
        id={"right-control"}
        className={"float top right"}
      >
        <Button
          variant={"primary"}
          className={"button false"}
          onClick={handleRefresh}>
          
          <RefreshSvg color={"var(--primary)"} />
        </Button>
      </div>
      <CustomOverlay
        id={"shipping-tooltip"}
        className={"tooltip"}
        position={hoveredShippingTooltip?.position}
      >
        <ShippingsTooltip shippings={hoveredShippingTooltip?.shippings} />
      </CustomOverlay>
    </OpenLayers>
  </div>);
});

export default memo(ShippingMap);
